// `links`: Swift `LinkConfig` (GroundTeam/Models/Record.swift): configurable links shown on records (or globally) with
// URL variables filled from the record. Decoding template `LinkConfig(name: "", urlTemplate: "")`
// (GroundTeam/Sync/ResilientDecoding.swift:108).

import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import {
  BOOL,
  STRING,
  UUID,
  createModel,
  decodeModel,
  field,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface LinkPayload extends Passthrough {
  id: string;
  name: string;
  /** URL with LINK_VARIABLES placeholders. */
  urlTemplate: string;
  isGlobal: boolean;
  /** SF Symbol name. */
  systemImage: string;
}

export const LINK_KEYS = [
  'id',
  'name',
  'urlTemplate',
  'isGlobal',
  'systemImage',
] as const satisfies readonly (keyof Known<LinkPayload>)[];

/** `LinkConfig.variables`. */
export const LINK_VARIABLES = [
  '{{latitude}}',
  '{{longitude}}',
  '{{address}}',
  '{{name}}',
  '{{phone}}',
  '{{email}}',
  '{{external_id}}',
] as const;

export const DEFAULT_LINK_IMAGE = 'link';

export const LINK_FIELDS: FieldMap<LinkPayload> = {
  id: field.strict(UUID),
  name: field.req(STRING, () => ''),
  urlTemplate: field.req(STRING, () => ''),
  isGlobal: field.req(BOOL, () => false),
  systemImage: field.req(STRING, () => DEFAULT_LINK_IMAGE),
};

export function decodeLink(raw: JsonObject, envelopeId?: string): Decoded<LinkPayload> {
  return decodeModel(LINK_FIELDS, raw, {}, envelopeId);
}

export type LinkInit = Partial<Known<LinkPayload>> & { name: string; urlTemplate: string };

export function newLink(init: LinkInit): LinkPayload {
  return createModel(LINK_FIELDS, { id: newId(), isGlobal: false, systemImage: DEFAULT_LINK_IMAGE }, init, 'links');
}
